/**
 * A past shift's slip, printed again from the Reports tab.
 *
 * The slip is the one the cashier was handed at close — same component, same
 * lines — but the figures come from the server now, so a shift that has had
 * bills refiled onto it prints corrected. Shown in the same sheet the day's
 * report prints from: a preview, then Close or Print.
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { ShiftReport } from '../pos/ShiftReport.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

export function ShiftPrintSheet({ shiftId, onClose }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['reports', 'shift-slip', shiftId],
    queryFn: () => api.get(`/reports/shifts/${shiftId}`),
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Print shift report"
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
      onClick={onClose}
    >
      <div className="w-full max-w-sm rounded-2xl bg-surface p-4" onClick={(e) => e.stopPropagation()}>
        <div className="max-h-[70vh] overflow-y-auto rounded-lg border border-rule p-4 text-center">
          {isLoading && <Spinner />}
          {error && (
            <p role="alert" className="text-sm text-bad">
              {error.message || 'The shift could not be loaded.'}
            </p>
          )}
          {data && <ShiftReport result={data} />}
        </div>
        <div className="no-print mt-3 flex gap-2">
          <Button variant="secondary" size="lg" className="flex-1" onClick={onClose}>
            Close
          </Button>
          <Button size="lg" className="flex-1" disabled={!data} onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </div>
    </div>
  );
}
